import { cn } from '@/lib/utils'
import {
  actionLabel,
  contactLine,
  type FieldColumn,
  needsOrganisationChoice,
  ORGANISATION_NEW,
  ORGANISATION_NONE,
  organisationLines,
  organisationValue,
  parseOrganisationValue,
} from './rowChoices'
import { StatusChip } from './StatusChip'
import type { ImportMode, PreviewRow as Verdict, RowChoice, RowField, RowInput } from './types'

interface PreviewRowProps {
  mode: ImportMode
  input: RowInput
  row: Verdict
  choice: RowChoice
  lead: FieldColumn
  others: readonly FieldColumn[]
  selected: boolean
  /** The row this one repeats is skipped (its choice, or an error): a merge goes nowhere. */
  targetSkipped: boolean
  onSelect: (selected: boolean) => void
  onEdit: (field: RowField, value: string) => void
  onLeave: () => void
  onChoice: (choice: RowChoice) => void
}

const SELECT = 'h-8 rounded-md border border-border bg-background px-2 text-sm'

/**
 * One row of ③ Controleren: the status, the lead cell (name or organisation) and the action
 * first (they decide the row), then the other editable cells; below, why.
 */
export function PreviewRow({
  mode,
  input,
  row,
  choice,
  lead,
  others,
  selected,
  targetSkipped,
  onSelect,
  onEdit,
  onLeave,
  onChoice,
}: PreviewRowProps) {
  const errorFields = new Set(
    row.issues.filter((issue) => issue.level === 'error').map((issue) => issue.field),
  )
  const organisationLinesText = organisationLines(row)
  const contact = contactLine(row)
  const askOrganisation = row.organisation_choice_required
  const details =
    row.duplicate_of_row !== null ||
    contact !== null ||
    organisationLinesText.length > 0 ||
    row.issues.length > 0 ||
    askOrganisation

  function cell({ field, label, width }: FieldColumn) {
    // The first and last name were joined into `name` when the rows were built.
    const failing = errorFields.has(field) || (field === 'name' && (errorFields.has('first_name') || errorFields.has('last_name')))
    return (
      <td key={field} className="py-2 pr-2">
        <input
          value={input[field]}
          onChange={(event) => onEdit(field, event.target.value)}
          onBlur={onLeave}
          aria-label={`${label}, rij ${row.row_number}`}
          aria-invalid={failing || undefined}
          className={cn(
            'h-8 rounded-md border bg-background px-2 text-sm',
            width,
            failing ? 'border-danger' : 'border-border',
          )}
        />
      </td>
    )
  }

  return (
    <>
      <tr className="border-t border-border align-top">
        <td className="py-2 pr-2">
          <input
            type="checkbox"
            checked={selected}
            onChange={(event) => onSelect(event.target.checked)}
            aria-label={`Selecteer rij ${row.row_number}`}
          />
        </td>
        <td className="py-2 pr-2">
          <StatusChip status={row.status} />
        </td>
        {cell(lead)}
        <td className="py-2 pr-2">
          <div className="flex flex-col gap-1">
            <select
              aria-label={`Actie, rij ${row.row_number}`}
              value={choice.action ?? ''}
              onChange={(event) =>
                onChoice({
                  ...choice,
                  action: (event.target.value || null) as RowChoice['action'],
                })
              }
              className={cn(SELECT, 'w-60')}
            >
              {choice.action === null ? <option value="">Kies…</option> : null}
              {row.actions.map((action) => (
                <option key={action} value={action}>
                  {actionLabel(action, row, mode)}
                </option>
              ))}
            </select>
            {mode === 'organisaties' && choice.action === 'link' && row.organisation.similar.length > 1 ? (
              <select
                aria-label={`Organisatie om mee te koppelen, rij ${row.row_number}`}
                value={choice.targetOrganisationId ?? ''}
                onChange={(event) => onChoice({ ...choice, targetOrganisationId: event.target.value })}
                className={cn(SELECT, 'w-60')}
              >
                {row.organisation.similar.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.city ? ` (${item.city})` : ''}
                  </option>
                ))}
              </select>
            ) : null}
            {askOrganisation ? (
              <select
                aria-label={`Organisatie, rij ${row.row_number}`}
                aria-invalid={needsOrganisationChoice(row, choice) || undefined}
                value={organisationValue(choice)}
                onChange={(event) => onChoice({ ...choice, ...parseOrganisationValue(event.target.value) })}
                className={cn(SELECT, 'w-60')}
              >
                <option value="">Organisatie: kies…</option>
                {row.organisation.similar.map((item) => (
                  <option key={item.id} value={`match:${item.id}`}>
                    {`Koppelen aan ${item.name}${item.city ? ` (${item.city})` : ''}`}
                  </option>
                ))}
                <option value={ORGANISATION_NEW}>Nieuwe organisatie</option>
                <option value={ORGANISATION_NONE}>Zonder organisatie</option>
              </select>
            ) : null}
          </div>
        </td>
        {others.map(cell)}
      </tr>
      {details ? (
        <tr>
          <td />
          <td />
          <td colSpan={others.length + 2} className="pb-2 text-xs">
            {row.duplicate_of_row !== null ? (
              <p className="text-muted-foreground">Dubbel in bestand: hetzelfde als rij {row.duplicate_of_row}.</p>
            ) : null}
            {targetSkipped && choice.action === 'merge' ? (
              <p className="text-warning">
                Let op: rij {row.duplicate_of_row} wordt overgeslagen, dus deze rij ook.
              </p>
            ) : null}
            {contact ? <p className="text-muted-foreground">↳ {contact}</p> : null}
            {organisationLinesText.map((line) => (
              <p key={line} className="text-muted-foreground">
                ↳ {line}
              </p>
            ))}
            {askOrganisation && needsOrganisationChoice(row, choice) ? (
              <p className="text-warning">Kies of de organisatie een bestaande is of een nieuwe.</p>
            ) : null}
            {row.issues.map((issue) => (
              <p
                key={`${issue.field ?? 'rij'}-${issue.message}`}
                className={issue.level === 'error' ? 'text-danger' : 'text-warning'}
              >
                {issue.level === 'error' ? 'Fout: ' : 'Let op: '}
                {issue.message}
              </p>
            ))}
          </td>
        </tr>
      ) : null}
    </>
  )
}
